import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@material-ui/core';
import { CommonDataGrid } from 'features/shared/components/table';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { getPersonHistory } from '../../services/userService';
import { HISTORY_COLUMNS } from '../../constants/personHistoryConstants';
import { calculateChangesRowHeight, MIN_ROW_HEIGHT } from '../../utility/personHistoryHelper';

const PersonHistoryChangesGrid = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalChanges, setTotalChanges] = useState(0);
  const [pageState, setPageState] = useState({ personId, page: 0, pageSize: 25 });
  const page = pageState.personId === personId ? pageState.page : 0;
  const pageSize = pageState.personId === personId ? pageState.pageSize : 25;

  const handleGridStateChange = useCallback(
    ({ page: nextPage, pageSize: nextPageSize }) => {
      setPageState((previous) =>
        previous.personId === personId &&
        previous.page === nextPage &&
        previous.pageSize === nextPageSize
          ? previous
          : { personId, page: nextPage, pageSize: nextPageSize }
      );
    },
    [personId]
  );

  /**
   * Pre-compute a Map<rowId, height> whenever the rows change.
   * DataGridPro calls getRowHeight({ id }) — we look up the pre-computed value
   */
  const rowHeightMap = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      map.set(row.id, calculateChangesRowHeight(row.changes));
    });

    return map;
  }, [rows]);

  const getRowHeight = useCallback(({ id }) => rowHeightMap.get(id) ?? MIN_ROW_HEIGHT, [
    rowHeightMap,
  ]);

  useEffect(() => {
    setRows([]);
    if (!personId) {
      setTotalChanges(0);
      setLoading(false);
      return;
    }

    let active = true;
    executeApiAction({
      dispatch,
      action: () => getPersonHistory(personId, { page: page + 1, pageSize }),
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      onSuccess: (response) => {
        if (!active) return;
        const data = response?.data || [];
        const pagination = response?.pagination || {};

        setRows(
          data.map((item, idx) => ({
            id: `${page}-${idx}`,
            timestamp: item.timestamp,
            changedBy: item.changedBy,
            event: item.event,
            changes: item.changes,
          }))
        );
        setTotalChanges(pagination.totalSnapshots ?? data.length);
      },
      onError: () => {
        if (active) setTotalChanges(0);
      },
    });

    return () => {
      active = false;
    };
  }, [dispatch, personId, page, pageSize]);

  return (
    <Box className="person-history-snapshots-grid">
      {loading && <FormSpinner />}
      <Box className="person-history-tab__toolbar" style={{ marginBottom: 8 }}>
        <Box className="person-history-tab__summary">
          {totalChanges > 0 && (
            <Typography variant="caption">
              {/* subtract one as there is one created record */}
              {totalChanges - 1} changes
            </Typography>
          )}
        </Box>
      </Box>

      <CommonDataGrid
        key={'change-history'}
        rows={rows}
        columns={HISTORY_COLUMNS}
        getRowId={(row) => row.id}
        getRowHeight={getRowHeight}
        paginationMode="server"
        serverSide={{
          totalRecords: totalChanges,
          onStateChange: handleGridStateChange,
        }}
        toolbar={getDataGridToolbarProps({
          showToolbar: false,
        })}
      />
    </Box>
  );
};

export default PersonHistoryChangesGrid;
